import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import { CartConfigService } from '@spartacus/cart/base/core';
import {
  ActiveCartFacade,
  Cart,
  OrderEntry,
  PromotionLocation,
  SelectiveCartFacade,
} from '@spartacus/cart/base/root';
import { AuthService, EventService, GlobalMessageType, RoutingService } from '@spartacus/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { AttemptToCreateQuoteWithContractProductsEvent } from 'src/app/core/events/gonvarri-attempt-create-quote-with-contract-products.event';
import { GonvarriProduct } from 'src/app/core/models/gonvarri-product.model';
import {GonvarriQuote} from "../../core/models/gonvarri-quote.model";

@Component({
    selector: 'cx-cart-details',
    templateUrl: './gonvarri-cart-details.component.html',
    styleUrl: './gonvarri-cart-details.component.scss',
    standalone: false
})
export class GonvarriCartDetailsComponent implements OnInit, OnChanges {
  @Input() customCart: Cart | undefined;

  cart$: Observable<Cart> = new Observable<Cart>();
  entries$: Observable<any[]> = new Observable<OrderEntry[]>();
  cartLoaded$: Observable<boolean> = new Observable<boolean>();
  loggedIn = false;
  promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;
  selectiveCartEnabled: boolean = true;

  contractProductsInCreateQuoteEvent$ = new BehaviorSubject<GonvarriProduct[]>([]);
  showQuoteCreationWarningMessage$ = new BehaviorSubject<boolean>(false);
  globalMessageType = GlobalMessageType;
  @Input() quote: GonvarriQuote | undefined;
  @Input() quoteIsReadOnly: boolean | undefined;
  @Input() showPromotions: boolean = true;

  constructor(
    protected activeCartService: ActiveCartFacade,
    protected selectiveCartService: SelectiveCartFacade,
    protected authService: AuthService,
    protected routingService: RoutingService,
    protected cartConfig: CartConfigService,
    protected eventService: EventService,
  ) {
    // Capture the event that is dispatched when the user tries to create a quote with products that have contractReference to show a warning message
    this.eventService.get(AttemptToCreateQuoteWithContractProductsEvent).subscribe(event => {
      this.contractProductsInCreateQuoteEvent$.next(event.products!);
      this.showQuoteCreationWarningMessage$.next(true);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    });
  }

  clearWarning(): void {
    this.showQuoteCreationWarningMessage$.next(false);
  }

  private cartReloaded = false;

  ngOnChanges(changes: SimpleChanges) {
    this.updateComponentData();
  }

  ngOnInit() {
    this.updateComponentData();
  }

  updateComponentData(): void {
    if(this.customCart === undefined && this.quote === undefined) {
      this.cart$ = this.activeCartService.getActive();
    }
    this.entries$ =
      this.quote ? of(this.quote.entries as any) :
        this.customCart ? of(this.customCart.entries as any) :
          this.activeCartService
            .getEntries()
            .pipe(filter((entries) => entries.length > 0));

    this.selectiveCartEnabled = this.cartConfig.isSelectiveCartEnabled();

    this.cartLoaded$ = combineLatest([
      this.activeCartService.isStable(),
      this.selectiveCartEnabled
        ? this.selectiveCartService.isStable()
        : of(false),
      this.authService.isUserLoggedIn(),
    ]).pipe(
      tap(([, , loggedIn]) => {
        this.loggedIn = loggedIn;
        if (loggedIn && !this.cartReloaded) {
          this.activeCartService.reloadActiveCart();
          this.cartReloaded = true;
        }
      }),
      map(([cartLoaded, sflLoaded, loggedIn]) =>
        loggedIn && this.selectiveCartEnabled
          ? cartLoaded && sflLoaded
          : cartLoaded
      )
    );
  }

  saveForLater(item: OrderEntry) {
    if (this.loggedIn) {
      this.activeCartService.removeEntry(item);
      this.selectiveCartService.addEntry(
        item.product?.code ?? '',
        item.quantity ?? 0
      );
    } else {
      this.routingService.go({ cxRoute: 'login' });
    }
  }

  getCartTotalItems(cart: any): number {
    return (cart?.totalItems) ?? 0;
  }

}
